import { useNavigate } from "react-router-dom";
import { useSearchContext } from "../Components/SearchContext/SearchContext";

/**
 * Material top-level groups shown in the primary navigation. These are
 * intentionally hardcoded (the API only models category -> subcategory); the
 * material dimension is derived here and passed to /products via router state.
 */
export enum NavMaterial {
  GOLD = "gold",
  SILVER = "silver",
  BRILIANT = "briliant",
  CUSTOM = "custom",
  ALL = "all",
}

const BASE_STATE = {
  material: null as string | null,
  briliant: null as boolean | null,
  category_id: null as number[] | null,
  subcategory_id: null as number[] | null,
  order: "desc",
  orderBy: "popular",
};

/**
 * Centralizes the "navigate to the product listing with a filter" behavior that
 * used to be duplicated across MainMenu. Every entry point performs the exact
 * same side-effects the listing page depends on: reset paging, fire the
 * `pageReset` event, and clear the active search term.
 */
const useCategoryNavigation = () => {
  const navigate = useNavigate();
  const { setSearch } = useSearchContext();

  const resetPaging = () => {
    sessionStorage.setItem("page", "0");
    window.dispatchEvent(new Event("pageReset"));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  const goToProducts = (state: typeof BASE_STATE) => {
    navigate("products", { state });
    resetPaging();
    setSearch("");
    scrollToTop();
  };

  /** Resolve a material group into the {material, briliant} filter pair. */
  const materialFilter = (material: NavMaterial) => {
    switch (material) {
      case NavMaterial.GOLD:
        return { material: "gold", briliant: null as boolean | null };
      case NavMaterial.SILVER:
        return { material: "silver", briliant: null as boolean | null };
      case NavMaterial.BRILIANT:
        return { material: "gold", briliant: true as const };
      default:
        return { material: null, briliant: null as boolean | null };
    }
  };

  /** Top-level material click (no category selected). */
  const goToMaterial = (material: NavMaterial) => {
    if (material === NavMaterial.ALL) {
      goToProducts({ ...BASE_STATE });
      return;
    }
    if (material === NavMaterial.CUSTOM) {
      navigate("about");
      setSearch("");
      scrollToTop();
      return;
    }
    goToProducts({ ...BASE_STATE, ...materialFilter(material) });
  };

  /** Material + category. */
  const goToCategory = (material: NavMaterial, categoryId: number) => {
    goToProducts({
      ...BASE_STATE,
      ...materialFilter(material),
      category_id: [categoryId],
    });
  };

  /** Material + category + subcategory. */
  const goToSubcategory = (
    material: NavMaterial,
    categoryId: number,
    subcategoryId?: number
  ) => {
    goToProducts({
      ...BASE_STATE,
      ...materialFilter(material),
      category_id: [categoryId],
      subcategory_id: subcategoryId !== undefined ? [subcategoryId] : null,
    });
  };

  return { goToMaterial, goToCategory, goToSubcategory };
};

export default useCategoryNavigation;
